import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import {
  fetchVideos,
  approveVideo,
  rejectVideo,
  unpublishVideo,
  requestAnalysis,
  webPlayableUrl,
  fetchModerationSettings,
  updateModerationSettings,
  type AdminVideo,
  type AnalysisVerdict,
  type ModerationSettings,
} from '../lib/videoService';
import { logger } from '../lib/logger';

type TabKey = 'pending_review' | 'approved' | 'rejected' | 'processing';
type SourceFilter = 'all' | 'candidate' | 'employer';
type MediaFilter = 'all' | 'video' | 'image';

const tabLabels: { key: TabKey; label: string }[] = [
  { key: 'pending_review', label: 'En attente' },
  { key: 'approved', label: 'Approuvées' },
  { key: 'rejected', label: 'Rejetées' },
  { key: 'processing', label: 'En traitement' },
];

const sourceFilters: { key: SourceFilter; label: string }[] = [
  { key: 'all', label: 'Toutes' },
  { key: 'candidate', label: 'Candidats' },
  { key: 'employer', label: 'Recruteurs' },
];

const mediaFilters: { key: MediaFilter; label: string }[] = [
  { key: 'all', label: 'Tous médias' },
  { key: 'video', label: 'Vidéos' },
  { key: 'image', label: 'Photos' },
];

function videoKey(v: Pick<AdminVideo, 'source' | 'id'>) {
  return `${v.source}-${v.id}`;
}

/** Upload / analyse (Mux ou image) encore en cours. */
function isTranscoding(v: AdminVideo) {
  return v.status === 'uploading' || v.status === 'analyzing';
}

/**
 * Relance possible : analyse échouée, ou post image resté bloqué en
 * 'analyzing' (déclenchement client jamais arrivé).
 */
function canRetryAnalysis(v: AdminVideo) {
  if (v.mediaType === 'image') {
    return v.analysisStatus === 'failed' || v.status === 'analyzing';
  }
  return v.analysisStatus === 'failed' && !!v.muxPlaybackId;
}

/** Badge « droits confirmés » / « sans consentement » (CGU art. 4). */
function RightsBadge({ video, compact = false }: { video: AdminVideo; compact?: boolean }) {
  if (video.rightsConsentAt) {
    return (
      <span
        className="px-2 py-0.5 text-[10px] font-semibold rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200"
        title={`Consentement droits / droit à l'image confirmé le ${video.rightsConsentAt}`}
      >
        {compact ? 'Droits ✓' : `Droits confirmés le ${video.rightsConsentAt}`}
      </span>
    );
  }
  return (
    <span
      className="px-2 py-0.5 text-[10px] font-semibold rounded-full border bg-amber-50 text-amber-800 border-amber-200"
      title="Aucune case de consentement droits / droit à l'image cochée à la publication"
    >
      Sans consentement droits
    </span>
  );
}

/** Rejet IA d'une vidéo recruteur : l'admin doit encore accepter ou refuser. */
function needsAdminDecision(v: AdminVideo) {
  return v.source === 'employer' && v.status === 'pending_review' && v.analysisVerdict === 'reject';
}

function inProcessingTab(v: AdminVideo) {
  return isTranscoding(v) || needsAdminDecision(v);
}

const verdictBadge: Record<AnalysisVerdict, { label: string; cls: string }> = {
  approve: { label: 'IA : Conforme', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  review: { label: 'IA : À vérifier', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  reject: { label: 'IA : Refus', cls: 'bg-red-50 text-red-600 border-red-200' },
};

function ScoreBar({ label, score }: { label: string; score: number | null | undefined }) {
  const v = typeof score === 'number' ? Math.max(0, Math.min(100, score)) : null;
  const color = v === null ? 'bg-gray-300' : v >= 75 ? 'bg-emerald-500' : v >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[11px] text-gray-500">{label}</span>
        <span className="text-[11px] font-semibold text-gray-700">{v === null ? '—' : `${v}/100`}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        {v !== null && <div className={`h-full rounded-full ${color}`} style={{ width: `${v}%` }} />}
      </div>
    </div>
  );
}

/** Panneau « Analyse IA » affiché dans le lecteur plein écran. */
function AnalysisPanel({ video }: { video: AdminVideo }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const report = video.analysisReport;

  const isImage = video.mediaType === 'image';
  const noun = isImage ? 'La photo' : 'La vidéo';

  if (video.analysisStatus === 'failed') {
    return (
      <div className="bg-white rounded-xl p-4 space-y-2">
        <p className="text-sm text-red-600">
          L'analyse IA a échoué — {noun.toLowerCase()} doit être modérée manuellement.
        </p>
        <RightsBadge video={video} />
      </div>
    );
  }
  if (!report) {
    return (
      <div className="bg-white rounded-xl p-4 space-y-2">
        <p className="text-sm text-gray-400">
          {video.analysisStatus === 'running' || video.analysisStatus === 'pending'
            ? 'Analyse IA en cours…'
            : 'Pas d\'analyse IA pour cette publication.'}
        </p>
        <RightsBadge video={video} />
      </div>
    );
  }

  const verdict = video.analysisVerdict ? verdictBadge[video.analysisVerdict] : null;

  return (
    <div className="bg-white rounded-xl p-5 space-y-4 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          Analyse IA {isImage ? '· photo' : '· vidéo'}
        </h3>
        {verdict && (
          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${verdict.cls}`}>
            {verdict.label}
          </span>
        )}
      </div>

      {/* Droit à l'image / droits sur le contenu */}
      <div className="flex items-center gap-2">
        <RightsBadge video={video} />
      </div>

      {/* Modération */}
      {report.moderation && (
        <div className={`rounded-lg px-3 py-2 text-xs ${report.moderation.flagged ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {report.moderation.flagged
            ? `⚠ Contenu signalé : ${report.moderation.categories.join(', ')}`
            : '✓ Aucun contenu offensant détecté'}
        </div>
      )}

      {/* Droits d'auteur — musique */}
      {report.music && (
        <div className={`rounded-lg px-3 py-2 text-xs ${
          report.music.licensed
            ? 'bg-red-50 text-red-700'
            : report.music.skipped
              ? 'bg-gray-50 text-gray-500'
              : 'bg-emerald-50 text-emerald-700'
        }`}>
          {report.music.licensed ? (
            <>
              ⚠ Musique sous licence détectée
              {(report.music.artist || report.music.title) && (
                <span className="block mt-0.5 font-medium">
                  {[report.music.artist, report.music.title].filter(Boolean).join(' — ')}
                  {report.music.label ? ` · ${report.music.label}` : ''}
                </span>
              )}
              {report.music.catalogs && report.music.catalogs.length > 0 && (
                <span className="block text-[10px] mt-0.5 opacity-80">
                  Catalogue : {report.music.catalogs.join(', ')}
                </span>
              )}
            </>
          ) : report.music.skipped ? (
            <>Identification musicale indisponible{report.music.error ? ` (${report.music.error})` : ''}</>
          ) : (
            '✓ Aucune œuvre musicale commerciale identifiée'
          )}
        </div>
      )}

      {/* Droits d'auteur — marques */}
      {report.brands && (
        <div className={`rounded-lg px-3 py-2 text-xs ${
          report.brands.detected ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-700'
        }`}>
          {report.brands.detected ? (
            <>
              ⚠ Marques potentiellement problématiques
              {(report.brands.brands || []).map((b, i) => (
                <span key={`${b.name}-${i}`} className="block mt-0.5">
                  <span className="font-medium">{b.name}</span>
                  {typeof b.confidence === 'number' ? ` (${Math.round(b.confidence * 100)} %)` : ''}
                  {b.context ? ` — ${b.context}` : ''}
                </span>
              ))}
              {report.brands.summary && (
                <span className="block mt-1 whitespace-pre-wrap break-words">{report.brands.summary}</span>
              )}
            </>
          ) : (
            '✓ Aucune grande marque détectée à l\'image'
          )}
        </div>
      )}

      {/* Conformité métier */}
      {report.conformity && (
        <div className="space-y-2">
          <ScoreBar label="Conformité métier" score={report.conformity.conformity_score} />
          {report.conformity.summary && (
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
              {report.conformity.summary}
            </p>
          )}
        </div>
      )}

      {/* Qualité du geste (expérimental) */}
      {report.style && (
        <div className="space-y-1 pt-1 border-t border-gray-100">
          <p className="text-[11px] font-medium text-gray-500">Style (informatif)</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
            {typeof report.style.humour === 'number' && (
              <span>Humour {Math.round(report.style.humour * 100)}%</span>
            )}
            {report.style.mood && Object.entries(report.style.mood).map(([k, v]) => (
              typeof v === 'number' ? <span key={k}>{k} {Math.round(v * 100)}%</span> : null
            ))}
            {report.style.rythme && Object.entries(report.style.rythme).map(([k, v]) => (
              typeof v === 'number' ? <span key={k}>{k} {Math.round(v * 100)}%</span> : null
            ))}
            {report.style.recit && Object.entries(report.style.recit).map(([k, v]) => (
              typeof v === 'number' ? <span key={`r-${k}`}>{k} {Math.round(v * 100)}%</span> : null
            ))}
          </div>
        </div>
      )}

      {report.technique && (
        <div className="space-y-2 pt-1 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <ScoreBar label="Qualité du geste (expérimental)" score={report.technique.technique_score} />
          </div>
          {(report.technique.criteria || []).map((c) => (
            <div key={c.name} className="text-xs text-gray-600 pl-2 border-l-2 border-gray-100 whitespace-pre-wrap break-words">
              <span className="font-medium text-gray-700">{c.name}</span>
              {typeof c.score === 'number' ? ` — ${c.score}/100` : ' — non observable'}
              {c.comment ? ` · ${c.comment}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Transcript */}
      {report.transcript !== undefined && (
        <div className="pt-1 border-t border-gray-100">
          <button
            onClick={() => setShowTranscript((s) => !s)}
            className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800"
          >
            {showTranscript ? 'Masquer la transcription' : 'Voir la transcription'}
          </button>
          {showTranscript && (
            <p className="text-sm text-gray-700 mt-1.5 leading-relaxed whitespace-pre-wrap break-words">
              {report.transcript || '(aucune parole détectée)'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const VideoModeration: React.FC = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState<AdminVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all');
  const [activeTab, setActiveTab] = useState<TabKey>('pending_review');
  const [verdictFilter, setVerdictFilter] = useState<'all' | AnalysisVerdict>('all');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  /** Publication ouverte dans le lecteur plein écran (vidéo ou image). */
  const [playing, setPlaying] = useState<AdminVideo | null>(null);
  /** Échec de lecture (rendition mp4 pas encore prête, ou codec hérité). */
  const [playError, setPlayError] = useState(false);
  /** Modal réglages modération. */
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<ModerationSettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const load = () => {
    fetchVideos()
      .then(setVideos)
      .catch((err) => logger.error('load_videos', 'VideoModeration', { error: String(err) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sameVideo = (a: Pick<AdminVideo, 'source' | 'id'>, b: Pick<AdminVideo, 'source' | 'id'>) =>
    a.source === b.source && a.id === b.id;
  const bySource = videos.filter(
    (v) => (sourceFilter === 'all' || v.source === sourceFilter)
      && (mediaFilter === 'all' || v.mediaType === mediaFilter),
  );
  const sourceCounts = {
    all: videos.length,
    candidate: videos.filter((v) => v.source === 'candidate').length,
    employer: videos.filter((v) => v.source === 'employer').length,
  };
  const mediaCounts = {
    all: videos.length,
    video: videos.filter((v) => v.mediaType === 'video').length,
    image: videos.filter((v) => v.mediaType === 'image').length,
  };
  const filteredVideos = bySource.filter((v) => {
    const inTab = activeTab === 'processing'
      ? inProcessingTab(v)
      : activeTab === 'pending_review'
        ? v.status === 'pending_review' && !needsAdminDecision(v)
        : v.status === activeTab;
    if (!inTab) return false;
    if (verdictFilter !== 'all' && activeTab === 'pending_review') {
      return v.analysisVerdict === verdictFilter;
    }
    return true;
  });

  const handleApprove = async (video: AdminVideo) => {
    if (!user) return;
    setActionLoading(videoKey(video));
    try {
      await approveVideo(video.id, user.id, video.source);
      setVideos((prev) =>
        prev.map((v) => sameVideo(v, video) ? { ...v, status: 'approved' as const, reviewedAt: new Date().toLocaleDateString('fr-FR') } : v)
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (video: AdminVideo) => {
    if (!user || !rejectReason.trim()) return;
    setActionLoading(videoKey(video));
    try {
      await rejectVideo(video.id, user.id, rejectReason, video.source);
      setVideos((prev) =>
        prev.map((v) => sameVideo(v, video) ? { ...v, status: 'rejected' as const, rejectionReason: rejectReason, reviewedAt: new Date().toLocaleDateString('fr-FR') } : v)
      );
      setRejectingId(null);
      setRejectReason('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublish = async (video: AdminVideo) => {
    if (!user) return;
    setActionLoading(videoKey(video));
    try {
      await unpublishVideo(video.id, user.id, video.source);
      setVideos((prev) =>
        prev.map((v) => sameVideo(v, video) ? { ...v, status: 'pending_review' as const, rejectionReason: null } : v)
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryAnalysis = async (video: AdminVideo) => {
    setActionLoading(videoKey(video));
    try {
      await requestAnalysis(video.id, video.source);
      alert('Analyse relancée — le rapport apparaîtra dans quelques instants.');
      load();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openSettings = async () => {
    setShowSettings(true);
    if (!settings) {
      try {
        setSettings(await fetchModerationSettings());
      } catch (err) {
        alert(`Erreur chargement réglages: ${err}`);
        setShowSettings(false);
      }
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSettingsSaving(true);
    try {
      await updateModerationSettings(settings);
      setShowSettings(false);
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setSettingsSaving(false);
    }
  };

  const formatDuration = (s: number | null) => {
    if (!s) return '—';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex p-1 bg-gray-100 rounded-xl" role="tablist" aria-label="Source des publications">
            {sourceFilters.map((opt) => {
              const active = sourceFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSourceFilter(opt.key)}
                  className={`px-3.5 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-white text-[#00244F] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                  <span className={`ml-1.5 text-[11px] font-semibold ${active ? 'text-[#00244F]' : 'text-gray-400'}`}>
                    {sourceCounts[opt.key]}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="inline-flex p-1 bg-gray-100 rounded-xl" role="tablist" aria-label="Type de média">
            {mediaFilters.map((opt) => {
              const active = mediaFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMediaFilter(opt.key)}
                  className={`px-3.5 py-2 text-sm font-medium rounded-lg transition-colors ${
                    active
                      ? 'bg-white text-[#00244F] shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                  <span className={`ml-1.5 text-[11px] font-semibold ${active ? 'text-[#00244F]' : 'text-gray-400'}`}>
                    {mediaCounts[opt.key]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <button
          onClick={openSettings}
          className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors"
        >
          ⚙ Réglages modération
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {tabLabels.map((tab) => {
            const count = tab.key === 'processing'
              ? bySource.filter(inProcessingTab).length
              : tab.key === 'pending_review'
                ? bySource.filter((v) => v.status === 'pending_review' && !needsAdminDecision(v)).length
                : bySource.filter((v) => v.status === tab.key).length;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.key
                    ? 'text-[#00244F] bg-blue-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                  activeTab === tab.key ? 'bg-[#00244F] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {count}
                </span>
                {activeTab === tab.key && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00244F]"></span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filtre verdict IA (onglet En attente) */}
        {activeTab === 'pending_review' && (
          <div className="px-5 py-2 border-b border-gray-100 flex items-center gap-2">
            <span className="text-xs text-gray-400">Verdict IA :</span>
            {(['all', 'approve', 'review', 'reject'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVerdictFilter(v)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                  verdictFilter === v
                    ? 'bg-[#00244F] text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {v === 'all' ? 'Tous' : verdictBadge[v].label.replace('IA : ', '')}
              </button>
            ))}
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {filteredVideos.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-400">
              Aucune publication dans cette catégorie
            </div>
          ) : (
            filteredVideos.map((video) => (
              <div key={videoKey(video)} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50/50 transition-colors">
                {/* Vignette cliquable → lecteur plein écran */}
                <button
                  onClick={() => { setPlayError(false); setPlaying(video); }}
                  className="w-24 h-16 rounded-lg flex items-center justify-center text-white text-2xl flex-shrink-0 relative overflow-hidden bg-gray-300 group cursor-pointer"
                  title="Visionner"
                >
                  {video.thumbnailUrl ? (
                    <img src={video.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="opacity-70">▶</span>
                  )}
                  <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="w-8 h-8 rounded-full bg-white/90 text-[#00244F] flex items-center justify-center text-sm">▶</span>
                  </span>
                  <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1 rounded">
                    {video.mediaType === 'image' ? 'IMG' : formatDuration(video.durationSeconds)}
                  </span>
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{video.candidateName}</p>
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${
                      video.source === 'employer'
                        ? 'bg-purple-50 text-purple-700 border-purple-200'
                        : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    }`}>
                      {video.source === 'employer' ? 'Recruteur' : 'Candidat'}
                    </span>
                    {video.mediaType === 'image' && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full border bg-sky-50 text-sky-700 border-sky-200">
                        Photo
                      </span>
                    )}
                    <RightsBadge video={video} compact />
                    {video.analysisVerdict && (
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${verdictBadge[video.analysisVerdict].cls}`}>
                        {verdictBadge[video.analysisVerdict].label}
                      </span>
                    )}
                    {video.analysisStatus === 'failed' && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full border bg-gray-100 text-gray-500 border-gray-200">
                        Analyse échouée
                      </span>
                    )}
                    {video.analysisReport?.music?.licensed && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full border bg-red-50 text-red-600 border-red-200">
                        Musique sous licence
                      </span>
                    )}
                    {video.analysisReport?.brands?.detected && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                        Marques
                      </span>
                    )}
                  </div>
                  {video.analysisReport?.conformity?.summary && (
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap break-words leading-relaxed">
                      {video.analysisReport.conformity.summary}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Soumise le {video.submittedAt}</p>
                  {video.reviewedAt && (
                    <p className="text-xs text-gray-400">Revue le {video.reviewedAt}</p>
                  )}
                  {video.rejectionReason && (
                    <p className="text-xs text-red-500 mt-1">Motif: {video.rejectionReason}</p>
                  )}
                </div>

                {isTranscoding(video) && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-200">
                      {video.status === 'uploading' ? 'Upload en cours' : 'Analyse IA en cours'}
                    </span>
                    {video.mediaType === 'image' && video.status === 'analyzing' && (
                      <button
                        onClick={() => handleRetryAnalysis(video)}
                        disabled={actionLoading === videoKey(video)}
                        className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-40"
                        title="Relancer si l'analyse ne démarre pas"
                      >
                        Relancer l'analyse
                      </button>
                    )}
                  </div>
                )}

                {video.status === 'pending_review' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {needsAdminDecision(video) && (
                      <span className="px-3 py-1.5 bg-amber-50 text-amber-800 text-xs font-medium rounded-full border border-amber-200">
                        Rejet IA — décision admin
                      </span>
                    )}
                    {canRetryAnalysis(video) && (
                      <button
                        onClick={() => handleRetryAnalysis(video)}
                        disabled={actionLoading === videoKey(video)}
                        className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-40"
                      >
                        Relancer l'analyse
                      </button>
                    )}
                    {rejectingId === videoKey(video) ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Motif du rejet..."
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
                          autoFocus
                        />
                        <button
                          onClick={() => handleReject(video)}
                          disabled={!rejectReason.trim() || actionLoading === videoKey(video)}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-40"
                        >
                          Confirmer
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectReason(''); }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleApprove(video)}
                          disabled={actionLoading === videoKey(video)}
                          className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
                        >
                          ✓ Approuver
                        </button>
                        <button
                          onClick={() => setRejectingId(videoKey(video))}
                          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          ✕ Rejeter
                        </button>
                      </>
                    )}
                  </div>
                )}

                {video.status === 'approved' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                      ✓ Approuvée
                    </span>
                    <button
                      onClick={() => handleUnpublish(video)}
                      disabled={actionLoading === videoKey(video)}
                      className="px-3 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-40"
                    >
                      Dépublier
                    </button>
                  </div>
                )}

                {video.status === 'rejected' && (
                  <span className="px-3 py-1.5 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200 flex-shrink-0">
                    ✕ Rejetée
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Lecteur plein écran (vidéo HTML5 / image) + panneau analyse IA */}
      {playing && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 overflow-y-auto py-6"
          onClick={() => setPlaying(null)}
        >
          <div className="relative flex flex-col md:flex-row items-start gap-5 mx-4 max-w-7xl w-full justify-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-w-md flex-shrink-0">
              <div className="bg-black rounded-2xl overflow-hidden relative" style={{ aspectRatio: '9/16', maxHeight: '80vh' }}>
                {playing.mediaType === 'image' ? (
                  <img src={playing.videoUrl} alt="" className="w-full h-full object-contain" />
                ) : playError ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 gap-3">
                    <span className="text-4xl">⚠️</span>
                    <p className="text-white text-sm font-medium">Vidéo pas encore lisible</p>
                    <p className="text-white/60 text-xs">
                      La rendition mp4 est peut-être en cours de génération (nouvel upload),
                      ou le fichier est dans un format hérité. Réessayez dans quelques secondes.
                    </p>
                    <a
                      href={webPlayableUrl(playing.videoUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    >
                      Ouvrir / télécharger le fichier
                    </a>
                  </div>
                ) : (
                  <video
                    src={webPlayableUrl(playing.videoUrl)}
                    controls
                    autoPlay
                    playsInline
                    className="w-full h-full object-contain"
                    onError={() => setPlayError(true)}
                  />
                )}
                {playing.textOverlay && (
                  <div className="absolute left-0 right-0 top-1/3 flex justify-center pointer-events-none px-6">
                    <p className="bg-black/50 text-white text-sm font-bold rounded-lg px-3 py-1.5 text-center">
                      {playing.textOverlay}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-3">
                <p className="text-white text-sm font-medium">
                  {playing.candidateName}
                  <span className="ml-2 text-white/60 text-xs font-normal">
                    {playing.source === 'employer' ? 'Recruteur' : 'Candidat'}
                    {' · '}
                    {playing.mediaType === 'image' ? 'Photo' : 'Vidéo'}
                  </span>
                </p>
                <div className="flex items-center gap-2">
                  {playing.status === 'pending_review' && (
                    <>
                      <button
                        onClick={() => { handleApprove(playing); setPlaying(null); }}
                        className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                      >
                        ✓ Approuver
                      </button>
                      <button
                        onClick={() => { setRejectingId(videoKey(playing)); setPlaying(null); }}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                      >
                        ✕ Rejeter
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setPlaying(null)}
                    className="px-4 py-2 text-sm font-medium text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>

            {/* Panneau analyse IA (vidéos et photos) */}
            <div className="w-full md:w-[28rem] lg:w-[36rem] flex-shrink-0 min-w-0">
              <AnalysisPanel video={playing} />
            </div>
          </div>
        </div>
      )}

      {/* Modal réglages modération */}
      {showSettings && settings && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowSettings(false)}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Réglages modération</h3>
              <p className="text-xs text-gray-400 mt-1">
                Gating hybride : validation humaine d'abord, auto-publication quand la confiance est établie.
                S'applique à toutes les publications (vidéos et photos).
              </p>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mode</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={settings.mode === 'human_first'}
                      onChange={() => setSettings({ ...settings, mode: 'human_first' })}
                      className="mt-0.5"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Validation humaine</strong>
                      <span className="block text-xs text-gray-400">
                        Toutes les publications candidat (vidéos et photos) passent en modération, avec le rapport IA en aide à la décision.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={settings.mode === 'ai_auto'}
                      onChange={() => setSettings({ ...settings, mode: 'ai_auto' })}
                      className="mt-0.5"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Auto-publication IA</strong>
                      <span className="block text-xs text-gray-400">
                        Verdict IA conforme → publiée directement ; douteuse → file humaine ; refus → rejetée + notification.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seuil d'auto-publication : {settings.autoApproveThreshold}/100
                </label>
                <input
                  type="range" min={50} max={100}
                  value={settings.autoApproveThreshold}
                  onChange={(e) => setSettings({ ...settings, autoApproveThreshold: Number(e.target.value) })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seuil d'auto-rejet : {settings.autoRejectThreshold}/100
                </label>
                <input
                  type="range" min={0} max={50}
                  value={settings.autoRejectThreshold}
                  onChange={(e) => setSettings({ ...settings, autoRejectThreshold: Number(e.target.value) })}
                  className="w-full"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={saveSettings}
                disabled={settingsSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
              >
                {settingsSaving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoModeration;
